/* ==========================================================================
   GR- Sticky Header — Growthly
   assets/gr-sticky-header.js

   Registered as a custom element so the Shopify theme editor lifecycle is
   handled natively: connectedCallback initialises, disconnectedCallback tears
   down. No per-section inline script, no duplicated listeners.

   PERFORMANCE
   - One shared scroll listener for every header on the page (there is normally
     one, but duplicates in the editor are handled for free).
   - Passive listener + rAF throttle; the handler only toggles classes.
   - Reads that could force layout are cached and only refreshed on resize.
   - All animation is CSS transform/opacity; JS never writes geometry per frame.
   ========================================================================== */
(() => {
  'use strict';

  if (customElements.get('gr-sticky-header')) return;

  const REDUCED = () => matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---- one scroll loop shared by all instances ---------------------------- */
  const instances = new Set();
  let ticking = false;
  let lastY = window.scrollY;

  const frame = () => {
    ticking = false;
    const y = window.scrollY;
    const delta = y - lastY;
    instances.forEach((el) => el.onScrollFrame(y, delta));
    lastY = y;
  };

  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(frame);
  };

  let resizeTimer = null;
  const onResize = () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      instances.forEach((el) => el.measure());
    }, 150);
  };

  const startLoop = () => {
    if (instances.size !== 1) return;
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });
  };
  const stopLoop = () => {
    if (instances.size) return;
    window.removeEventListener('scroll', onScroll);
    window.removeEventListener('resize', onResize);
  };

  class GRStickyHeader extends HTMLElement {
    connectedCallback() {
      this.root = this.querySelector('[data-header]');
      if (!this.root) return;

      // ------------------------------------------------------------------
      // Escape every ancestor stacking context.
      //
      // Shopify nests each section in a wrapper, and a theme (or an app) can
      // give any ancestor a transform, filter, opacity, backdrop-filter,
      // contain or will-change. Any one of those makes the wrapper the
      // containing block for our position:fixed bar, which then gets painted
      // *inside* that section and can be covered by later ones. No z-index
      // value can beat that, because the comparison happens in the wrong
      // context.
      //
      // The only reliable fix is to reparent the bar to <body>, where no such
      // ancestor exists. The spacer stays where the section was, so the page
      // still reserves the right amount of room.
      // ------------------------------------------------------------------
      this.portal();

      this.bar = this.root.querySelector('[data-bar]');
      this.inner = this.root.querySelector('[data-inner]');
      this.toggle = this.root.querySelector('[data-toggle]');
      this.drawer = this.root.querySelector('[data-drawer]');
      this.spacer = this.querySelector('[data-spacer]');

      this.shrinkEnabled = this.dataset.shrink === 'true';
      this.collapseLinks = this.dataset.collapseLinks === 'true';
      this.hideOnDown = this.dataset.hideOnDown === 'true';
      this.shrinkStart = parseFloat(this.dataset.shrinkStart) || 40;
      this.compactWidth = parseFloat(this.dataset.compactWidth) || 460;
      this.compactHeight = parseFloat(this.dataset.compactHeight) || 54;

      if (this.collapseLinks) this.root.classList.add('collapse-links');

      this.designMode = !!(window.Shopify && window.Shopify.designMode);

      this.bindDrawer();
      this.measure();

      instances.add(this);
      startLoop();
      this.onScrollFrame(window.scrollY, 0);
    }

    disconnectedCallback() {
      instances.delete(this);
      stopLoop();
      this.unbindDrawer();
      document.documentElement.style.overflow = '';
      // return the bar so Shopify can remove the section cleanly
      if (this._portaled && this.root && this.root.parentNode === document.body) {
        document.body.removeChild(this.root);
      }
    }

    /* ---- move the fixed bar out to <body> ---------------------------------- */
    portal() {
      if (this._portaled) return;
      // In the theme editor keep it in place so section selection still works.
      if (window.Shopify && window.Shopify.designMode) return;
      if (this.root.parentNode === document.body) return;
      document.body.appendChild(this.root);
      this._portaled = true;
    }

    /* ---- measurement: run once, and again on resize ---------------------- */
    measure() {
      if (!this.inner) return;
      const rect = this.inner.getBoundingClientRect();
      const w = rect.width;
      const h = rect.height;
      if (!w || !h) return;

      const desktop = window.innerWidth >= 990;

      // Scale factors that turn the full-width bar into the compact pill.
      const targetW = desktop ? Math.min(this.compactWidth, w) : w;
      const targetH = desktop ? this.compactHeight : Math.min(this.compactHeight + 6, h);

      this.root.style.setProperty('--gr-h-pill-sx', (targetW / w).toFixed(4));
      this.root.style.setProperty('--gr-h-pill-sy', (targetH / h).toFixed(4));

      // How far the side slots travel inward so they stay inside the pill.
      const slide = desktop ? Math.max(0, (w - targetW) / 2 - 4) : 0;
      this.root.style.setProperty('--gr-h-slide', slide.toFixed(1) + 'px');

      // Reserve the expanded height so the page never shifts when it shrinks.
      if (this.spacer) this.spacer.style.height = this.bar.offsetHeight + 'px';
    }

    /* ---- scroll ----------------------------------------------------------- */
    onScrollFrame(y, delta) {
      if (this.designMode) return;

      if (this.shrinkEnabled) {
        const shrunk = y > this.shrinkStart;
        if (shrunk !== this._shrunk) {
          this._shrunk = shrunk;
          this.root.classList.toggle('is-scrolled', shrunk);
        }
      }

      if (this.hideOnDown && !this.isOpen) {
        if (y <= this.shrinkStart) {
          if (this._hidden) { this._hidden = false; this.root.classList.remove('is-hidden'); }
        } else if (Math.abs(delta) > 6 && y > this.shrinkStart * 3) {
          const hide = delta > 0;
          if (hide !== this._hidden) {
            this._hidden = hide;
            this.root.classList.toggle('is-hidden', hide);
          }
        }
      }
    }

    /* ---- drawer ----------------------------------------------------------- */
    get isOpen() { return this.root.classList.contains('drawer-open'); }

    bindDrawer() {
      if (!this.toggle || !this.drawer) return;

      this._onToggle = () => { this.isOpen ? this.close() : this.open(); };
      this._onDrawerClick = (e) => {
        if (e.target.closest('a[href]')) { this.close({ restoreFocus: false }); return; }
        // Clicking the blurred backdrop (anywhere outside the panel) closes it.
        if (!e.target.closest('.gr-header__drawer-inner')) this.close();
      };
      this._onKeydown = (e) => {
        if (!this.isOpen) return;
        if (e.key === 'Escape') { e.preventDefault(); this.close(); return; }
        if (e.key !== 'Tab') return;
        const items = [this.toggle].concat(
          Array.from(this.drawer.querySelectorAll('a[href], button:not([disabled])'))
        );
        if (!items.length) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      };

      this.toggle.addEventListener('click', this._onToggle);
      this.drawer.addEventListener('click', this._onDrawerClick);
      document.addEventListener('keydown', this._onKeydown);
    }

    unbindDrawer() {
      if (this.toggle && this._onToggle) this.toggle.removeEventListener('click', this._onToggle);
      if (this.drawer && this._onDrawerClick) this.drawer.removeEventListener('click', this._onDrawerClick);
      if (this._onKeydown) document.removeEventListener('keydown', this._onKeydown);
    }

    open() {
      if (!this.drawer || !this.toggle) return;
      this._lastFocus = document.activeElement;
      this.drawer.hidden = false;
      requestAnimationFrame(() => this.root.classList.add('drawer-open'));
      this.toggle.setAttribute('aria-expanded', 'true');
      document.documentElement.style.overflow = 'hidden';
      const first = this.drawer.querySelector('a[href], button:not([disabled])');
      if (first) setTimeout(() => first.focus({ preventScroll: true }), REDUCED() ? 0 : 240);
    }

    close({ restoreFocus = true } = {}) {
      if (!this.drawer || !this.toggle) return;
      this.root.classList.remove('drawer-open');
      this.toggle.setAttribute('aria-expanded', 'false');
      document.documentElement.style.overflow = '';
      const finish = () => { this.drawer.hidden = true; };
      REDUCED() ? finish() : setTimeout(finish, 320);
      if (restoreFocus && this._lastFocus && this._lastFocus.focus) {
        this._lastFocus.focus({ preventScroll: true });
      }
    }
  }

  customElements.define('gr-sticky-header', GRStickyHeader);

  /* ---- theme editor: keep the bar visible while editing ------------------- */
  document.addEventListener('shopify:section:select', (e) => {
    const root = e.target.querySelector('[data-header]');
    if (root) root.classList.remove('is-hidden');
  });
})();
